import React, { useState } from 'react';
import type { EquipmentProcessType } from '../../features/equipment/equipmentTypes';

const ADD_CUSTOM_VALUE = '__add_custom_process_type__';

type Props = {
  value: string;
  options: readonly EquipmentProcessType[];
  onChange: (value: EquipmentProcessType | '') => void;
  onAddCustom?: (value: string) => EquipmentProcessType | null;
  allowNone?: boolean;
  className: string;
};

const ProcessTypeSelect: React.FC<Props> = ({
  value,
  options,
  onChange,
  onAddCustom,
  allowNone = false,
  className,
}) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  const submit = () => {
    const processType = onAddCustom?.(draft);
    if (!processType) return;
    onChange(processType);
    setDraft('');
    setAdding(false);
  };

  return (
    <div className="min-w-0">
      <select
        value={value}
        className={className}
        onChange={(event) => {
          if (event.target.value === ADD_CUSTOM_VALUE) {
            setAdding(true);
            return;
          }
          onChange(event.target.value as EquipmentProcessType | '');
        }}
      >
        {allowNone && <option value="">None</option>}
        {options.map((processType) => (
          <option key={processType} value={processType}>
            {processType}
          </option>
        ))}
        {onAddCustom && <option value={ADD_CUSTOM_VALUE}>+ Add custom process type…</option>}
      </select>

      {adding && (
        <div className="mt-1 flex gap-1">
          <input
            autoFocus
            value={draft}
            maxLength={40}
            placeholder="e.g. WET_CLEAN"
            aria-label="Custom process type name"
            className={className}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              } else if (event.key === 'Escape') {
                setAdding(false);
              }
            }}
          />
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim()}
            className="rounded border border-primary-300 px-2 text-[10px] font-semibold text-primary-700 disabled:opacity-40"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => setAdding(false)}
            className="rounded px-1.5 text-[10px] text-gray-500 hover:bg-gray-100"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default ProcessTypeSelect;
